import React, {
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from 'react';
import omit from 'lodash/omit';
import isArray from 'lodash/isArray';
import UploadFilesControl from 'chameleon/ui-stack/ui/forms/upload-files-control';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import Input from 'chameleon/ui-stack/ui/base/input';
import { userTypes, issueTypes, KEYS_MAP } from './constants';

const ContactUsForm = forwardRef((props, ref) => {
  const { onFormChange, userData } = props;
  const { isStudent } = userData;
  const labelClassName = 'text-14 font-medium text-black block mb-2';
  const textareaClassName =
    'px-2 w-full h-84 border border-solid border-gray-400 rounded-0.6 shadow-lg resize-none';

  const [lvl2Options, setLvl2Options] = useState([]);
  const [lvl3Options, setLvl3Options] = useState([]);

  const [inputValues, setInputValues] = useState({});
  const [selectedUserType, setSelectedUserType] = useState({});
  const [selectedIssueType, setSelectedIssueType] = useState({});
  const [selectedIssueTypeLvl2, setSelectedIssueTypeLvl2] = useState({});
  const [selectedIssueTypeLvl3, setSelectedIssueTypeLvl3] = useState({});

  const studentsInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const questionItemRef = useRef(null);
  const existingContentRef = useRef(null);
  const expectedContentRef = useRef(null);
  const uploadFilesRef = useRef(null);
  const pageUrlRef = useRef(null);

  const getDataPerUserType = useCallback((data, isStudent) => {
    if (isArray(data)) {
      return data;
    }

    if (isStudent) {
      return data?.student || [];
    }

    return data?.educator || [];
  }, []);

  const onIssueLvl1Change = useCallback(
    (selection) => {
      setInputValues((inputValues) => {
        return {
          ...omit(inputValues, ['areaIssueType', 'questionIssueType']),
        };
      });
      setSelectedIssueType(selection);
      setSelectedIssueTypeLvl2({});
      const { areaOfIssue, questionIssue } =
        issueTypes.find((option) => option.id === selection.id) || {};
      setLvl2Options(getDataPerUserType(areaOfIssue, isStudent));

      const lvl3Options = getDataPerUserType(questionIssue, isStudent);
      setSelectedIssueTypeLvl3(lvl3Options.length === 1 ? lvl3Options[0] : {});
      setLvl3Options(lvl3Options);
    },
    [issueTypes, isStudent],
  );

  const onIssueLvl2Change = useCallback(
    (selection) => {
      setSelectedIssueTypeLvl2(selection);
    },
    [issueTypes],
  );

  const onIssueLvl3Change = useCallback((selection) => {
    setSelectedIssueTypeLvl3(selection);
  }, []);

  const isContentTypeSelected = useMemo(
    () => selectedIssueType.id === KEYS_MAP.contentIssue,
    [selectedIssueType],
  );

  const isEnhancedRequestSelected = useMemo(
    () => selectedIssueType.id === KEYS_MAP.enhancedRequest,
    [selectedIssueType],
  );

  useImperativeHandle(ref, () => ({
    getUploadedFiles: () => {
      return uploadFilesRef.current.getUploadedFiles();
    },
    clearForm: () => {
      setSelectedUserType({});
      setSelectedIssueType({});
      setSelectedIssueTypeLvl2({});
      setSelectedIssueTypeLvl3({});
      setLvl3Options([]);
      setLvl2Options([]);
      if (studentsInputRef.current) {
        studentsInputRef.current.value = '';
      }
      if (descriptionRef.current) {
        descriptionRef.current.value = '';
      }
      uploadFilesRef.current.clearFiles();
    },
  }));

  const isTeacherHelping = useMemo(
    () => selectedUserType.id === KEYS_MAP.helping,
    [selectedUserType],
  );

  const onStudentsChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        students: value,
      };
    });
  }, []);

  const onPageUrlChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        pageUrl: value,
      };
    });
  }, []);

  const onQuestionItemChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        questionItem: value,
      };
    });
  }, []);

  const onExistingContentChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        existingContent: value,
      };
    });
  }, []);

  const onExpectedContentChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        expectedContent: value,
      };
    });
  }, []);

  const onDescriptionChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((inputValues) => {
      return {
        ...inputValues,
        description: value,
      };
    });
  }, []);

  useEffect(() => {
    setInputValues((inputs) => {
      if (isContentTypeSelected) {
        return {
          ...omit(inputs, ['description']),
          questionItem: questionItemRef.current.value,
          existingContent: existingContentRef.current.value,
          expectedContent: expectedContentRef.current.value,
          pageUrl: pageUrlRef.current.value,
        };
      }

      return {
        ...omit(inputs, [
          'questionItem',
          'existingContent',
          'expectedContent',
          'pageUrl',
        ]),
        description: descriptionRef.current.value,
      };
    });
  }, [isContentTypeSelected]);

  useEffect(() => {
    const values = {
      ...inputValues,
      issueType: selectedIssueType.label,
    };
    if (!isStudent) {
      values.userType = selectedUserType.label;
    }
    if (
      ![KEYS_MAP.other, KEYS_MAP.enhancedRequest].includes(selectedIssueType.id)
    ) {
      values.areaIssueType = selectedIssueTypeLvl2.label;
      values.questionIssueType = selectedIssueTypeLvl3.label;
    }
    onFormChange(values, {
      isContentTypeSelected,
      selectedIssueType,
    });
  }, [
    inputValues,
    selectedUserType,
    selectedIssueType,
    selectedIssueTypeLvl2,
    selectedIssueTypeLvl3,
    isContentTypeSelected,
  ]);

  const issueTypeOptions = useMemo(() => {
    if (isStudent) {
      return issueTypes.filter((item) => item.id !== KEYS_MAP.contentIssue);
    }

    return issueTypes;
  }, [issueTypes, isStudent]);

  return (
    <>
      <div className="text-13 col-span-3">Required fields *</div>
      {!isStudent ? (
        <>
          <div>
            <label className={labelClassName}>Whose issue is it?*</label>
            <AutocompleteSelect
              portalContainerClassName="z-overModals"
              className="w-full"
              placeholder={{ label: 'Select...' }}
              options={userTypes}
              onChange={(selection) => {
                if (selection.id === KEYS_MAP.needHelp) {
                  if (studentsInputRef.current) {
                    studentsInputRef.current.value = '';
                  }
                  setInputValues((inputValues) => {
                    return {
                      ...omit(inputValues, ['students']),
                    };
                  });
                } else {
                  setInputValues((inputValues) => {
                    return {
                      ...inputValues,
                      students: '',
                    };
                  });
                }
                setSelectedUserType(selection);
              }}
              value={selectedUserType}
            />
          </div>
          <div className="col-span-2">
            {isTeacherHelping ? (
              <>
                <label className={labelClassName}>
                  Full Names of Students Experiencing Issue *
                </label>
                <Input
                  ref={studentsInputRef}
                  onChange={onStudentsChange}
                  placeholder={
                    'Eg. Provide list of students (first and last names)'
                  }
                />
              </>
            ) : null}
          </div>
        </>
      ) : null}
      <div>
        <label className={labelClassName}>Type of issue *</label>
        <AutocompleteSelect
          placeholder={{ label: 'Select a Type' }}
          portalContainerClassName="z-overModals"
          className="w-full"
          options={issueTypeOptions}
          onChange={onIssueLvl1Change}
          value={selectedIssueType}
        />
      </div>
      <div>
        <label className={labelClassName}>Area of issue *</label>
        <AutocompleteSelect
          disableDropdown={lvl2Options.length === 0}
          placeholder={{ label: 'Select...' }}
          portalContainerClassName="z-overModals"
          className="w-full"
          options={lvl2Options}
          onChange={onIssueLvl2Change}
          value={selectedIssueTypeLvl2}
        />
      </div>
      <div>
        <label className={labelClassName}>Question/Issue *</label>
        <AutocompleteSelect
          disableDropdown={lvl3Options.length === 0}
          placeholder={{ label: 'Select...' }}
          portalContainerClassName="z-overModals"
          className="w-full"
          options={lvl3Options}
          onChange={onIssueLvl3Change}
          value={selectedIssueTypeLvl3}
        />
      </div>
      {isContentTypeSelected ? (
        <>
          <div className="col-span-3">
            <label htmlFor="page-url" className={labelClassName}>
              Link to Assessment/Question *
            </label>
            <Input
              ref={pageUrlRef}
              onChange={onPageUrlChange}
              id="page-url"
              placeholder="Provide the url of the relevant page for review."
            />
          </div>
          <div className="col-span-3">
            <label htmlFor="question-item-details" className={labelClassName}>
              Question/Item Details *
            </label>
            <textarea
              id="question-item-details"
              onChange={onQuestionItemChange}
              ref={questionItemRef}
              placeholder="Provide the assessment and question item details (e.g., Assessment name, Question #)"
              className={textareaClassName}
              defaultValue=""
            ></textarea>
          </div>
          <div className="col-span-3">
            <label htmlFor="existing-content" className={labelClassName}>
              Description of Existing Content *
            </label>
            <textarea
              id="existing-content"
              onChange={onExistingContentChange}
              ref={existingContentRef}
              placeholder="Provide an example of how the text/content is currently displayed."
              className={textareaClassName}
              defaultValue=""
            ></textarea>
          </div>
          <div className="col-span-3">
            <label htmlFor="expected-content" className={labelClassName}>
              Recommended/Expected Content *
            </label>
            <textarea
              id="expected-content"
              onChange={onExpectedContentChange}
              ref={expectedContentRef}
              placeholder="Provide the recommended or expected text/content."
              className={textareaClassName}
              defaultValue=""
            ></textarea>
          </div>
        </>
      ) : (
        <div className="col-span-3">
          <label htmlFor="description" className={labelClassName}>
            Description *
          </label>
          <div className="text-gray-700 text-14 mb-2">
            Provide a description and relevant details.
          </div>
          <textarea
            id="description"
            onChange={onDescriptionChange}
            ref={descriptionRef}
            placeholder={
              isEnhancedRequestSelected
                ? 'Please submit your suggestions for review by a member of our team.'
                : 'eg. Provide the URL and the assignment/assessment name'
            }
            className={textareaClassName}
            defaultValue=""
          />
        </div>
      )}
      <div className="col-span-3">
        <UploadFilesControl
          ref={uploadFilesRef}
          label="Share Screenshots"
          labelClassName="text-black block mb-2"
        />
      </div>
    </>
  );
});

export default ContactUsForm;
